import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ANALYTICAL_DATASET_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';

import { projectPageKeys } from '../lib/api/queryKeys';

/**
 * Subscribes to analytical-dataset write mutations and invalidates both the
 * project artifact manifest and activity feed when any AD save/process
 * completes — whether or not the project-page routes are the active view.
 *
 * Mounted once in ProjectPage so it stays active across the full project
 * session, including while the user is on the analytical-dataset detail
 * sub-route (where the manifest and activity queries are unmounted). When
 * those queries remount on navigation back, TQ sees them as invalidated and
 * refetches immediately.
 *
 * Mirrors `useProjectPageCohortSync`: the AD write mutations live in
 * `feature-analytical-datasets`, which this lib can't import (build cycle), so
 * the bridge is the mutation cache plus a shared mutation-key tag from
 * core-constants.
 */
export const useProjectPageAnalyticalDatasetSync = (projectId: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        event.mutation.options.mutationKey?.[0] === ANALYTICAL_DATASET_WRITE_MUTATION_KEY
      ) {
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.artifactsForProject(projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.activityForProject(projectId),
        });
      }
    });
  }, [queryClient, projectId]);
};
